import createCache, { EmotionCache } from "@emotion/cache";
import { useTheme } from "@mui/material/styles";
import { createMakeAndWithStyles } from "tss-react";
import { withStyles as tssWithStyles } from "tss-react/mui";

// JSS ordered sheets by `index`, then by when makeStyles was called (module load, deps first),
// so a component's own styles beat the shared ones it builds on. tss inserts on first render instead,
// which flips that, so every sheet gets its own cache anchored to a <meta> kept in JSS order
type Sheet = { index: number; marker: HTMLMetaElement };
const sheets: Sheet[] = [];
let autoIndex = -1e9;

const toLetters = (n: number): string =>
  (n >= 26 ? toLetters(Math.floor(n / 26) - 1) : "") +
  String.fromCharCode(97 + (n % 26));

const createSheetCache = (index = autoIndex++): EmotionCache => {
  const key = `tss-${toLetters(sheets.length)}`;
  const marker = document.createElement("meta");
  marker.setAttribute("data-tss-sheet", key);
  const position = sheets.findIndex((sheet) => sheet.index > index);
  const next = position === -1 ? null : sheets[position];
  document.head.insertBefore(marker, next ? next.marker : null);
  sheets.splice(position === -1 ? sheets.length : position, 0, {
    index,
    marker,
  });
  return createCache({ key, insertionPoint: marker });
};

// JSS also sorted every rule's props by (kebab) name length, so paddingTop beat padding no matter
// the order they were written in. emotion keeps the written order, so do the same sort here
const hyphenate = (prop: string) =>
  prop.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`).replace(/^ms-/, "-ms-");

const byLength = (a: string, b: string) => {
  const [propA, propB] = [hyphenate(a), hyphenate(b)];
  if (propA.length === propB.length) return propA > propB ? 1 : -1;
  return propA.length - propB.length;
};

const isNestedRule = (value: unknown) =>
  !!value && typeof value === "object" && !Array.isArray(value);

const sortProps = (style: Record<string, any>): Record<string, any> => {
  const keys = Object.keys(style);
  const props = keys.filter((key) => !isNestedRule(style[key])).sort(byLength);
  const nested = keys.filter((key) => isNestedRule(style[key]));
  return Object.fromEntries([
    ...props.map((key) => [key, style[key]]),
    ...nested.map((key) => [key, sortProps(style[key])]),
  ]);
};

const sortRules = (rules: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(rules).map(([name, style]) => [
      name,
      isNestedRule(style) ? sortProps(style) : style,
    ])
  );

const withSortedRules = (css: any): any =>
  typeof css === "function"
    ? (...args: any[]) => sortRules(css(...args))
    : sortRules(css);

type SheetParams = {
  name?: string | Record<string, unknown>;
  uniqId?: string;
  index?: number;
};

export function makeStyles<
  Params = void,
  RuleNameSubsetReferencableInNestedSelectors extends string = never,
>({ index, ...params }: SheetParams = {}) {
  const makeTssStyles = createMakeAndWithStyles({
    useTheme,
    cache: createSheetCache(index),
  }).makeStyles<Params, RuleNameSubsetReferencableInNestedSelectors>(params);

  return ((css) => makeTssStyles(withSortedRules(css))) as typeof makeTssStyles;
}

export const withStyles: typeof tssWithStyles = Object.assign(
  ((Component, cssObjectByRuleNameOrGetCssObjectByRuleName, params) =>
    createMakeAndWithStyles({
      useTheme,
      cache: createSheetCache(),
    }).withStyles(
      Component,
      withSortedRules(cssObjectByRuleNameOrGetCssObjectByRuleName),
      params
    )) as typeof tssWithStyles,
  { getClasses: tssWithStyles.getClasses }
);
